import { useEffect, useRef, useState, useCallback } from 'react';

export const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ──────────────────────────────────────────────────────────────
   Global reveal engine
   One IntersectionObserver drives every [data-rv], .mask-line
   and .edge on the page. Elements flip to .in and stay there.
   ────────────────────────────────────────────────────────────── */
export function useRevealEngine(active = true) {
  useEffect(() => {
    if (!active) return undefined;

    const SELECTOR = '[data-rv], .mask-line, .edge';
    const targets = document.querySelectorAll(SELECTOR);

    if (reduced()) {
      targets.forEach((el) => el.classList.add('in'));
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('in'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    const watch = (el) => {
      if (el.classList.contains('in')) return;
      io.observe(el);
    };

    targets.forEach(watch);

    /* Content rendered later — an expanded list, a toggled panel — was never
       in the first query, so pick it up as it arrives. */
    const mo = new MutationObserver((records) => {
      records.forEach((rec) => {
        rec.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches(SELECTOR)) watch(node);
          node.querySelectorAll(SELECTOR).forEach(watch);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    /* Safety net: content must never stay invisible because an observer
       misbehaved. Anything still hidden after a few seconds is revealed. */
    const rescue = setInterval(() => {
      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.classList.contains('in')) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('in');
      });
    }, 2500);

    return () => { io.disconnect(); mo.disconnect(); clearInterval(rescue); };
  }, [active]);
}

/* ──────────────────────────────────────────────────────────────
   Scroll position + progress, rAF-throttled
   ────────────────────────────────────────────────────────────── */
export function useScrollMeter() {
  const [meter, setMeter] = useState({ y: 0, progress: 0, dir: 'down' });
  const last = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const read = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setMeter({
        y,
        progress: max > 0 ? Math.min(1, y / max) : 0,
        dir: y > last.current ? 'down' : 'up',
      });
      last.current = y;
      ticking.current = false;
    };
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return meter;
}

/* ──────────────────────────────────────────────────────────────
   Count-up — eased, rAF driven, fires once when `run` is true
   ────────────────────────────────────────────────────────────── */
export function useCountUp(target, run, duration = 1600) {
  const [value, setValue] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    if (!run || done.current) return undefined;
    done.current = true;

    if (reduced()) {
      setValue(target);
      return undefined;
    }

    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, duration]);

  return value;
}

/* ──────────────────────────────────────────────────────────────
   Magnetic pointer attraction for buttons / interactive nodes
   ────────────────────────────────────────────────────────────── */
export function useMagnetic(strength = 0.28, cap = 16) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (reduced()) return undefined;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;

    const move = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      const x = Math.max(-cap, Math.min(cap, dx * strength));
      const y = Math.max(-cap, Math.min(cap, dy * strength));
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    const leave = () => { el.style.transform = 'translate3d(0,0,0)'; };

    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
    };
  }, [strength, cap]);

  return ref;
}

/* ──────────────────────────────────────────────────────────────
   In-view flag for a single element (for counters, canvases…)
   ────────────────────────────────────────────────────────────── */
export function useInViewOnce(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;

    const visible = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    };

    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);

    /* Fallback so a counter can never sit at zero in front of a visitor. */
    const poll = setInterval(() => {
      if (visible()) { setSeen(true); clearInterval(poll); io.disconnect(); }
    }, 1200);

    return () => { io.disconnect(); clearInterval(poll); };
  }, [seen, threshold]);

  return [ref, seen];
}

/* ──────────────────────────────────────────────────────────────
   Smooth anchor scrolling with header offset
   ────────────────────────────────────────────────────────────── */
export function useScrollTo() {
  return useCallback((id, offset = 88) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: reduced() ? 'auto' : 'smooth' });
  }, []);
}
